import React, {useCallback, useEffect, useMemo, useState} from "react";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import Chips, {ChipsContentType, ChipsLayoutTypes, ChipsTypes} from "../../../base/chips/chips";
import {Col} from "reactstrap";
import PartAutocomplete from "../../../text-fields/autocomplete/part-autocomplete";
import ProductInformationPopover from "../../../app-specific/draggable-popover/product-information-popover";

const MultiSelectPartSearchSection = ({
                                          defaultSelectedProduct,
                                          includeDefaultPart = true,
                                          parts,
                                          setParts,
                                      }) => {
    const [defaultPart, setDefaultPart] = useState(defaultSelectedProduct);

    /**
     * As soon as the component mounts:
     * - sets the default part if includeDefaultPart is true
     */
    useEffect(() => {
        if (!includeDefaultPart)
            return;
        setDefaultPart(defaultSelectedProduct);
    }, [defaultSelectedProduct, includeDefaultPart]);

    /**
     * Removes a customer from the list of selected customers.
     * @param part
     */
    const removePart = useCallback((part) => {
        setParts(parts => parts.filter(e => e.id !== part.id));
    }, [setParts]);

    /**
     * Depending on addingDefault:
     * if true => adds the default customer to the list of customers and removes the isMissing property.
     * if false => adds the customer to the list of customers
     * @param customer
     * @param addingDefault
     */
    const addPart = useCallback((part, addingDefault = false) => {
        let res = part;
        setParts(prev => {
            if (prev.map(e => e.partNo).includes(res.partNo)) {
                return prev;
            }
            if (addingDefault) {
                delete res['isMissing'];
            }
            return [res, ...prev];
        });
    }, [setParts]);

    const partElements = useMemo(() => {
        let res = parts;
        if (!!defaultPart) {
            const exists = !!res.find(e => e.id === defaultPart.id);
            if (!exists) {
                res = [{...defaultPart, isMissing: true}, ...res]
            }
        }
        return [
            ...res.map((part, index) => {
                if (index === 0 && defaultPart && part.id === defaultPart.id && part.isMissing) {
                    return (
                        <CSSTransition key={part.id} timeout={0} classNames="item">
                            <div className='mr-2 mt-2'>
                                <Chips
                                    type={ChipsTypes.add}
                                    layoutType={ChipsLayoutTypes.dashed}
                                    contentType={ChipsContentType.element}
                                    className={'part-number-fluid'}
                                    onIconClick={() => addPart(part, true)}
                                    content={
                                        <ProductInformationPopover partNo={part?.partNumber ?? part?.partNo}>
                                            {part?.partNumber ?? part?.partNo ?? "--"}
                                        </ProductInformationPopover>
                                    }
                                />
                            </div>
                        </CSSTransition>
                    )
                }
                return (
                    <CSSTransition key={part.id} timeout={500} classNames="item">
                        <div className=' mr-2 mt-2'>
                            <Chips
                                type={ChipsTypes.remove}
                                contentType={ChipsContentType.element}
                                className={'part-number-fluid'}
                                onIconClick={() => removePart(part)}
                                content={
                                    <ProductInformationPopover partNo={part?.partNumber ?? part?.partNo}>
                                        {part?.partNumber ?? part?.partNo ?? "--"}
                                    </ProductInformationPopover>
                                }
                            />
                        </div>
                    </CSSTransition>
                )
            }),
            <CSSTransition key={'input'} timeout={500} classNames="item">
                <Col xs={12} md={6} lg={5} xl={4} className='mt-2'>
                    <PartAutocomplete
                        clearOnBlur
                        filterOptions={(options) => options.filter(e => !parts.map(e => e.partNo ?? e.partNumber).includes(e.partNo))}
                        label={'Product'}
                        placeholder={'part number...'}
                        renderTags={() => ""}
                        value={null}
                        onChange={(_, part) => !!part ? addPart(part) : void 0}
                        textFieldProps={{
                            variant: 'filled',
                            InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                        }}
                    />
                </Col>
            </CSSTransition>

        ];
    }, [parts, defaultPart, addPart, removePart]);

    return (
        <>
            <TransitionGroup className="row no-gutters align-items-center justify-content-start flex-wrap h-100">
                {partElements}
            </TransitionGroup>
        </>
    );
}

export default MultiSelectPartSearchSection;
